import {
  faCheck,
  faChevronRight,
  faFire,
} from "@fortawesome/pro-regular-svg-icons"
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import theme, { FaIcon } from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"

import { SrOnly } from "@/components"
import useResponsiveImage from "@/hooks/useResponsiveImage"
import type { CategoryType } from "@/queries/models/Category"

const { md, lg, xl, xxl } = theme.breakpoints.values

const SIZES = [
  `(min-width: ${xxl}px) 300px`,
  `(min-width: ${xl}px) calc((100vw - 240px) / 4)`,
  `(min-width: ${lg}px) calc((100vw - 192px) / 3)`,
  `(min-width: ${md}px) calc((100vw - 144px) / 2)`,
  "calc(100vw - 96px)",
].join(", ")

const MAX_VISIBLE = 4

const StyledCard = styled(Card)({
  height: "100%",
  overflow: "visible",
  position: "relative",
})

const HeroPositioner = styled(Box)({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
})

const HeroImage = styled(CardMedia)(({ theme }) => ({
  width: `calc(100% - ${theme.spacing(8)})`,
  [theme.breakpoints.up("md")]: {
    width: 274,
  },
  aspectRatio: "2 / 1",
  objectFit: "cover",
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(-2),
})) as typeof CardMedia

const HeroOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(5),
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  background:
    "radial-gradient(ellipse at center, #333333 0%, rgba(51,51,51,0) 50%)",
  borderRadius: theme.spacing(2),
  pointerEvents: "none",
  marginTop: theme.spacing(2),
}))

const HeroTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  fontWeight: 700,
  textAlign: "center",
  ...theme.typography.h5,
})) as typeof Typography

const PopularChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: 8,
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 2,
  backgroundColor: "#EC0640",
  color: theme.palette.common.white,
  height: 24,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  fontSize: "0.75rem",
  fontWeight: 400,
  "& .MuiChip-icon": {
    color: theme.palette.common.white,
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    fontSize: "0.75rem",
  },
  "& .MuiChip-label": {
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  pointerEvents: "none",
}))

const StyledCardActionArea = styled(CardActionArea)({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  "& .MuiCardActionArea-focusHighlight": {
    display: "none",
  },
  "&:hover .MuiCardMedia-root": {
    opacity: 0.85,
    transition: "opacity 0.2s ease",
  },
})

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(3),
}))

const CheckIconWrap = styled(Box)({
  height: "1.43em",
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
})

type CategoryCardProps = {
  description: string
  imageId: number
  title: string
  features?: string[]
  popular?: boolean
  category: CategoryType
  onClick: () => void
}

const CategoryCard = ({
  description,
  imageId,
  title,
  features = [],
  popular,
  category,
  onClick,
}: CategoryCardProps) => {
  const { t } = useTranslation()
  const isDestinationPass = category === "DestinationPass"
  const { imageUrl, srcSet } = useResponsiveImage(imageId, {
    maxSize: isDestinationPass,
  })
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))
  const headingId = `pass-category-card-${category}-${imageId}-title`

  const visibleFeatures = features.slice(0, MAX_VISIBLE)
  const hasMore = features.length > MAX_VISIBLE
  const displayedFeatures = hasMore
    ? [...visibleFeatures, t("passCategoryCard.andMuchMore")]
    : visibleFeatures

  const featuresText = visibleFeatures.join(", ")

  return (
    <Box sx={{ pt: "calc((100% - 64px) / 2)", height: "100%" }}>
      <StyledCard>
        <StyledCardActionArea
          onClick={onClick}
          disableRipple
          aria-labelledby={headingId}
        >
          {isDestinationPass && (
            <SrOnly>
              <h3 id={headingId}>{title}</h3>
            </SrOnly>
          )}
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <HeroPositioner>
              <HeroImage
                component="img"
                image={imageUrl}
                srcSet={srcSet}
                sizes={SIZES}
                alt=""
              />
              {title && !isDestinationPass && (
                <HeroOverlay>
                  <HeroTitle id={headingId} component="h3">
                    {title}
                  </HeroTitle>
                </HeroOverlay>
              )}
              {popular && (
                <PopularChip
                  icon={<FaIcon icon={faFire} />}
                  label={t("passCategoryCard.popular")}
                />
              )}
            </HeroPositioner>
          </Box>

          <StyledCardContent>
            <Typography sx={{ typography: "body2", mb: 1 }}>
              {description}
            </Typography>

            {isDesktop && features.length > 0 && (
              <Stack spacing={0.5} mb={2}>
                {displayedFeatures.map((feature) => (
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1}
                    key={feature}
                  >
                    <CheckIconWrap>
                      <FaIcon icon={faCheck} sx={{ fontSize: "1rem" }} />
                    </CheckIconWrap>
                    <Typography sx={{ typography: "body2" }}>
                      {feature}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            {!isDesktop && features.length > 0 && (
              <Typography sx={{ typography: "body2", mb: 2 }}>
                {featuresText}
                {hasMore && ` ${t("passCategoryCard.andMuchMore")}`}
              </Typography>
            )}

            <Box mt="auto">
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                component="div"
                tabIndex={-1}
                role="presentation"
                disableRipple
                disableTouchRipple
                endIcon={
                  <FaIcon icon={faChevronRight} sx={{ fontSize: "0.8em" }} />
                }
              >
                {title}
              </Button>
            </Box>
          </StyledCardContent>
        </StyledCardActionArea>
      </StyledCard>
    </Box>
  )
}

export default CategoryCard
